import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { gamesToWin } from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { groupMatchLabel, playerName } from './labels';
import { GamesEditor, useGamesDraft } from './GamesEditor';
import { FootballMatchDialog } from './FootballScoreEditor';

interface CellEditorProps {
  cellId: string | null;
  open: boolean;
  onClose: () => void;
}

export function CellEditor({ cellId, open, onClose }: CellEditorProps) {
  const { tournament } = useTournament();
  // Football tournaments score a single goals result with an explicit "final"
  // action; everything else keeps the best-of games editor.
  if (tournament?.sportId === 'FOOTBALL') {
    return <FootballCellEditor cellId={cellId} open={open} onClose={onClose} />;
  }
  return <BestOfCellEditor cellId={cellId} open={open} onClose={onClose} />;
}

function FootballCellEditor({ cellId, open, onClose }: CellEditorProps) {
  const { tournament } = useTournament();
  const { t } = useTranslation();
  const cell = cellId ? (tournament?.cells[cellId] ?? null) : null;
  if (!cell || !tournament) return null;

  return (
    <FootballMatchDialog
      open={open}
      onClose={onClose}
      title={t('admin.groups.editor.title')}
      subtitle={t('admin.football.groupSubtitle', {
        matchLabel: groupMatchLabel(tournament, cell, t),
        timeSlot: cell.timeSlot,
        pitch: cell.tableIndex,
      })}
      matchId={cell.id}
      games={cell.games}
      events={cell.events}
      phase="group"
      refereeId={cell.refereeId}
      nameA={playerName(tournament, cell.playerA)}
      nameB={playerName(tournament, cell.playerB)}
      membersA={tournament.players[cell.playerA]?.members ?? []}
      membersB={tournament.players[cell.playerB]?.members ?? []}
    />
  );
}

function BestOfCellEditor({ cellId, open, onClose }: CellEditorProps) {
  const { tournament, updateCell } = useTournament();
  const { t } = useTranslation();
  const cell = cellId ? (tournament?.cells[cellId] ?? null) : null;

  // Hooks must run unconditionally; fall back to a harmless best-of when closed.
  const draft = useGamesDraft(open, cell?.games ?? [], cell?.bestOf ?? 1);

  if (!cell || !tournament) return null;

  const nameA = playerName(tournament, cell.playerA);
  const nameB = playerName(tournament, cell.playerB);

  const save = () => {
    if (!draft.validation.valid) return;
    void updateCell(cell.id, draft.cleaned);
    onClose();
  };

  const clear = () => {
    void updateCell(cell.id, []);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.groups.editor.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary">
          {t('admin.groups.editor.subtitle', {
            matchLabel: groupMatchLabel(tournament, cell, t),
            bestOf: cell.bestOf,
            gamesToWin: gamesToWin(cell.bestOf),
          })}
        </Typography>

        <GamesEditor
          draft={draft}
          bestOf={cell.bestOf}
          nameA={`${nameA} (${cell.playerA})`}
          nameB={`${nameB} (${cell.playerB})`}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={clear}>
          {t('admin.groups.editor.clear')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>{t('admin.common.cancel')}</Button>
        <Button variant="contained" disabled={!draft.validation.valid} onClick={save}>
          {t('admin.groups.editor.saveResult')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
